import {ApiClientError} from '@/api/client';
import {Link, useNavigate} from 'react-router-dom';
import {RoleSelector} from '@/components/team/RoleSelector';
import {SiteAccessCheckboxes} from '@/components/team/SiteAccessCheckboxes';
import {Spinner} from '@/components/ui/Spinner';
import {useInviteTeamMember} from '@/hooks/useInviteTeamMember';
import {useSites} from '@/hooks/useSites';
import {useState} from 'react';
import type {SiteId, TeamRole} from '@/types/api';

const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 48,
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 400,
    borderRadius: 8,
    letterSpacing: '-0.013em',
    border: '1px solid var(--tt-card-border)',
    color: 'var(--tt-text-primary)',
    background: 'var(--tt-card-bg)',
    outline: 'none',
    transition: 'border-color .15s linear, box-shadow .15s linear',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit'
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    margin: '0 0 .3em',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--tt-text-primary)'
};

function handleInputFocus(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'var(--tt-accent)';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239,90,43,.25)';
}

function handleInputBlur(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'var(--tt-card-border)';
    e.currentTarget.style.boxShadow = 'none';
}

export function InviteTeamMemberPage({modal = false, onSuccess}: {modal?: boolean; onSuccess?: () => void}) {
    const navigate = useNavigate();
    const {data: sites, isLoading: sitesLoading} = useSites();
    const inviteTeamMember = useInviteTeamMember();

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [role, setRole] = useState<TeamRole>('Editor');
    const [selectedSiteIds, setSelectedSiteIds] = useState<SiteId[]>([]);
    const [error, setError] = useState('');

    const activeSites = sites?.filter(s => s.status === 'active') ?? [];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Please enter an email address.');
            return;
        }
        if (!email.includes('@') || !email.includes('.')) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!name.trim()) {
            setError('Please enter a name.');
            return;
        }
        if (selectedSiteIds.length === 0) {
            setError('Please select at least one newsletter.');
            return;
        }

        try {
            await inviteTeamMember.mutateAsync({
                email: email.trim(),
                name: name.trim(),
                role,
                siteIds: selectedSiteIds
            });
            if (onSuccess) {
                onSuccess();
            } else {
                navigate('/team');
            }
        } catch (err) {
            if (err instanceof ApiClientError) {
                setError(err.message);
            } else {
                setError('Something went wrong. Please try again.');
            }
        }
    };

    const canSubmit =
        email.trim().length > 0 &&
        name.trim().length > 0 &&
        selectedSiteIds.length > 0 &&
        !inviteTeamMember.isLoading;

    if (sitesLoading) {
        return (
            <div className={modal ? 'studio-create-dialog-loading' : 'studio-content-page'}
                style={{flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--tt-main-bg)', maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}
            >
                <Spinner />
            </div>
        );
    }

    return (
        <div className={modal ? 'studio-create-dialog-form' : 'studio-content-page'}>
            {!modal && <header className="studio-detail-heading">
                <Link className="studio-back-link" to="/team">← Team</Link>
                <h1 className="studio-page-title">Invite a team member</h1>
                <p className="studio-page-description">Choose who can collaborate on your newsletters.</p>
            </header>}
            <div className={modal ? undefined : 'studio-form-panel'}>
                <form autoComplete="off" onSubmit={handleSubmit}>
                    <div style={{marginBottom: 20}}>
                        <label htmlFor="invite-email" style={labelStyle}>Email address</label>
                        <input autoComplete="off"
                            className="studio-field-control"
                            id="invite-email"
                            placeholder="colleague@example.com"
                            style={inputStyle}
                            type="email" value={email}
                            autoFocus
                            onBlur={handleInputBlur}
                            onChange={(e) => {
                                setEmail(e.target.value); setError('');
                            }}
                            onFocus={handleInputFocus}
                        />
                    </div>

                    <div style={{marginBottom: 20}}>
                        <label htmlFor="invite-name" style={labelStyle}>Name</label>
                        <input autoComplete="off"
                            className="studio-field-control"
                            id="invite-name"
                            placeholder="Jane Doe"
                            style={inputStyle}
                            type="text" value={name}
                            onBlur={handleInputBlur}
                            onChange={(e) => {
                                setName(e.target.value); setError('');
                            }}
                            onFocus={handleInputFocus}
                        />
                    </div>

                    <div style={{marginBottom: 24}}>
                        <RoleSelector value={role} onChange={setRole} />
                    </div>

                    <div style={{marginBottom: 24}}>
                        <SiteAccessCheckboxes
                            selectedIds={selectedSiteIds}
                            sites={activeSites}
                            onChange={setSelectedSiteIds}
                        />
                    </div>

                    {error && (
                        <div
                            style={{
                                marginTop: 16,
                                padding: '12px 16px',
                                background: 'rgba(245, 11, 35, 0.08)',
                                borderRadius: 8,
                                color: 'var(--tt-red)',
                                fontSize: '13px',
                                lineHeight: 1.5,
                                textAlign: 'center'
                            }}
                        >
                            {error.toLowerCase().includes('upgrade') ? (
                                <>
                                    {error.split(/upgrade/i)[0]}
                                    <Link style={{color: 'var(--tt-accent)', fontWeight: 500, textDecoration: 'underline'}} to="/billing/upgrade">upgrade</Link>
                                    {error.split(/upgrade/i)[1]}
                                </>
                            ) : error}
                        </div>
                    )}

                    <button className="studio-control studio-control-primary"
                        disabled={!canSubmit}
                        style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', margin: '32px 0 0', cursor: canSubmit ? 'pointer' : 'default', fontFamily: 'inherit', letterSpacing: '-0.005em'}}
                        type="submit"
                        onMouseEnter={(e) => {
                            if (canSubmit) {
                                e.currentTarget.style.backgroundColor = 'var(--tt-accent-hover)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (canSubmit) {
                                e.currentTarget.style.backgroundColor = 'var(--tt-accent)';
                            }
                        }}
                    >
                        {inviteTeamMember.isLoading ? (
                            <>
                                <svg height="16" style={{animation: 'spin 1s linear infinite'}} viewBox="0 0 16 16" width="16">
                                    <circle cx="8" cy="8" fill="none" r="6" stroke="currentColor" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" strokeWidth="2"/>
                                </svg>
                                Sending invitation...
                            </>
                        ) : (
                            <>Send invitation</>
                        )}
                    </button>
                </form>
            </div>

            <style>
                {`@keyframes spin { to { transform: rotate(360deg); } }`}
            </style>
        </div>
    );
}
